import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Copy, Check, X } from "lucide-react";

/**
 * Holiday / Sale banner — sits at the very top of the site above the free-shipping bar.
 * Auto-hides after EXPIRY_DATE so you don't have to remember to remove it.
 * Customers can copy a coupon code with one click.
 *
 * To remove the banner entirely, just delete the <PromoBanner /> line in Layout.jsx
 * (or set EXPIRY to a past date here).
 */

// CHANGE THIS to adjust the sale — auto-hides after this moment.
// Father's Day weekend 2026: Sun June 21. Expires Mon June 22 06:59 UTC (Sun 11:59pm Mountain)
const EXPIRY = new Date("2026-06-22T06:59:00Z");

const CODES = [
  { code: "DAD15", blurb: "15% off everything" },
  { code: "GENTLEMAN20", blurb: "20% off $50+" },
  { code: "FREESHIPDAD", blurb: "Free shipping $35+" },
];

const DISMISS_KEY = "dc_promo_fd_2026_dismissed";

export default function PromoBanner() {
  const [now, setNow] = useState(() => new Date());
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(DISMISS_KEY) === "1";
  });
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    // Refresh expiry check once a minute so it disappears the moment the sale ends
    const t = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(t);
  }, []);

  if (dismissed) return null;
  if (now >= EXPIRY) return null;

  const copy = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(code);
      setTimeout(() => setCopied(null), 1800);
    } catch (e) {
      // ignore — older browsers
    }
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  return (
    <div
      className="relative overflow-hidden bg-gradient-to-r from-[#1F3A2A] via-[#2E5C44] to-[#1F3A2A] text-[#FFF7E6]"
      data-testid="promo-banner"
    >
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 50%, #D9A85C 0%, transparent 35%), radial-gradient(circle at 80% 50%, #D9A85C 0%, transparent 35%)",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-2.5 sm:py-3 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2.5 min-w-0">
          <Sparkles className="w-4 h-4 text-[#D9A85C] shrink-0" />
          <div className="text-xs sm:text-sm">
            <span className="font-serif italic text-[#D9A85C] text-base sm:text-lg leading-none mr-2">
              Father&apos;s Day Weekend
            </span>
            <span className="opacity-90">— for the rugged ones in our lives.</span>
          </div>
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
          {CODES.map((c) => (
            <button
              key={c.code}
              onClick={() => copy(c.code)}
              className="group inline-flex items-center gap-1.5 bg-[#FFF7E6]/15 hover:bg-[#FFF7E6]/25 border border-[#D9A85C]/40 rounded-full px-2.5 py-1 text-[11px] sm:text-xs font-medium transition-colors"
              data-testid={`promo-code-${c.code}`}
              title={`Click to copy ${c.code}`}
            >
              {copied === c.code ? (
                <Check className="w-3 h-3 text-[#D9A85C]" />
              ) : (
                <Copy className="w-3 h-3 opacity-60 group-hover:opacity-100" />
              )}
              <span className="font-mono tracking-wider text-[#D9A85C]">{c.code}</span>
              <span className="hidden sm:inline opacity-75">· {c.blurb}</span>
            </button>
          ))}
          <Link
            to="/shop"
            className="hidden md:inline-flex items-center gap-1 bg-[#FFF7E6] text-[#1F3A2A] hover:bg-[#D9A85C] rounded-full px-3 py-1 text-xs font-semibold transition-colors"
            data-testid="promo-shop-btn"
          >
            Shop the sale →
          </Link>
        </div>

        <button
          onClick={dismiss}
          className="absolute right-2 top-1/2 -translate-y-1/2 sm:relative sm:right-auto sm:top-auto sm:translate-y-0 p-1 rounded-full hover:bg-[#FFF7E6]/15 transition-colors"
          aria-label="Dismiss banner"
          data-testid="promo-dismiss"
        >
          <X className="w-3.5 h-3.5 opacity-70" />
        </button>
      </div>
    </div>
  );
}
